import { memo, type CSSProperties } from 'react';
import { Avatar, Badge, IconButton, cn } from '@cyphex/ui';
import { X } from 'lucide-react';
import { KIND_META } from '../notificationMeta';
import { absoluteTime, isoTime, relativeTime } from '../relativeTime';
import type { NotificationItem } from '../types';

export interface NotificationRowProps {
  item: NotificationItem;
  onActivate: (item: NotificationItem) => void;
  onDismiss: (id: string) => void;
  /** Absolute-positioned by the virtualiser. */
  style?: CSSProperties;
}

/** One Activity-feed row. Fixed height so the virtualiser never measures. */
export const NotificationRow = memo(function NotificationRow({
  item, onActivate, onDismiss, style,
}: NotificationRowProps) {
  const meta = KIND_META[item.kind];
  const Glyph = meta.icon;
  const actor = item.preview.actorName ?? item.title;

  return (
    <div style={style} className="absolute left-0 top-0 w-full px-2">
      <div
        className={cn(
          'group flex h-[76px] items-center gap-3 rounded-lg px-2.5 transition-colors duration-fast',
          item.isRead ? 'bg-transparent' : 'bg-primary-subtle/60',
          'hover:bg-subtle',
        )}
      >
        <button
          type="button"
          onClick={() => onActivate(item)}
          className="flex min-w-0 flex-1 items-center gap-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span className="relative shrink-0">
            <Avatar name={actor} src={item.preview.actorAvatarUrl} size="lg" />
            <span
              className={cn(
                'absolute -bottom-1 -right-1 inline-flex h-5 w-5 items-center justify-center rounded-pill border-2 border-card bg-card',
                meta.tone,
              )}
            >
              <Glyph size={12} strokeWidth={2.25} aria-hidden />
            </span>
          </span>

          <span className="min-w-0 flex-1">
            <span className="flex items-baseline justify-between gap-3">
              <span className="truncate text-md font-semibold text-foreground">{item.title}</span>
              <time
                dateTime={isoTime(item.createdAt)}
                title={absoluteTime(item.createdAt)}
                className="shrink-0 text-sm text-muted-foreground"
              >
                {relativeTime(item.createdAt)}
              </time>
            </span>
            <span className="mt-0.5 line-clamp-1 block text-sm text-muted-foreground">
              {item.preview.scopeName ? `${item.preview.scopeName} · ` : ''}
              {item.preview.snippet ?? item.body ?? meta.label}
            </span>
            {item.groupCount > 1 ? (
              <span className="mt-1 inline-flex">
                <Badge count={item.groupCount} tone="brand-subtle" size="sm" />
              </span>
            ) : null}
          </span>
        </button>

        <span className="flex w-5 shrink-0 items-center justify-center">
          {!item.isRead ? <Badge dot tone="brand" className="group-hover:hidden" /> : null}
          <IconButton
            icon={X}
            label={`Dismiss: ${item.title}`}
            size="xs"
            variant="ghost"
            onClick={() => onDismiss(item.id)}
            className="hidden group-hover:inline-flex"
          />
        </span>
      </div>
    </div>
  );
});
